// src/components/molecules/SearchableMultiSelectFilter/SearchableMultiSelectFilter.jsx
import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  FormControl,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  TextField,
  InputAdornment,
  Box
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { operationalScrollbars } from '../../../design-system/tokens';

/**
 * SearchableMultiSelectFilter - Componente molecular para filtro multi-seleção com busca
 * 
 * Este componente permite selecionar múltiplas opções em um filtro com funcionalidade de busca.
 * Segue o princípio do Atomic Design como uma "molécula".
 */
const SearchableMultiSelectFilter = ({
  value,
  onChange,
  options = [],
  label = 'Selecionar',
  placeholder = 'Buscar...',
  className = '',
  size = 'small',
  ...props
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [open, setOpen] = useState(false);

  // Filtrar opções baseado no termo de busca (ordenação mantida da fonte original)
  const filteredOptions = useMemo(() => {
    if (!searchTerm.trim()) {
      return options;
    }
    
    return options.filter(option =>
      option.label.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [options, searchTerm]);

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSearchTerm(''); // Limpar busca ao fechar
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  return (
    <FormControl size={size} className={`searchable-multi-select-filter ${className}`} sx={{ minWidth: 140 }} {...props}>
      <Select
        displayEmpty
        multiple
        open={open}
        onOpen={handleOpen}
        onClose={handleClose}
        value={value || []}
        onChange={(e) => onChange(e.target.value)}
        renderValue={(selected) => {
          if (selected.length === 0) return label;
          if (selected.length === 1) {
            const option = options.find(opt => opt.value === selected[0]);
            return option ? option.label : label;
          }
          return `${label} (${selected.length})`;
        }}
        MenuProps={{
          PaperProps: {
            sx: {
              maxHeight: 300,
              width: 250,
              ...operationalScrollbars.default,
            },
          },
        }}
      >
        {/* Campo de busca */}
        <Box sx={{ p: 1, borderBottom: 1, borderColor: 'divider' }}>
          <TextField
            fullWidth
            size="small"
            placeholder={placeholder}
            value={searchTerm}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          />
        </Box>
        
        {/* Opções filtradas */}
        {filteredOptions.length > 0 ? (
          filteredOptions.map(option => (
            <MenuItem
              key={option.value}
              value={option.value}
            >
              <Checkbox checked={(value || []).indexOf(option.value) > -1} />
              <ListItemText primary={option.label} />
            </MenuItem>
          ))
        ) : (
          <MenuItem disabled>
            <ListItemText primary="Nenhum item encontrado" />
          </MenuItem>
        )}
      </Select>
    </FormControl>
  );
};

SearchableMultiSelectFilter.propTypes = {
  value: PropTypes.array,
  onChange: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired
    })
  ).isRequired,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  className: PropTypes.string,
  size: PropTypes.oneOf(['small', 'medium'])
};

export default SearchableMultiSelectFilter;
